import React from 'react';
import { isNil } from 'lodash';
// components
import Modal from 'shared/modals/Modal';
import ErrorMessage from 'shared/templates/ErrorMessage';
import TruckPermitRuleForm from '../../forms/TruckPermitRuleForm';
// fetch
import {
  upsertTruckPermitRule,
  getTruckPermitRuleTypes,
  getTruckPermitRuleFiles,
  addTruckPermitRuleFiles,
  upsertTruckPermitRuleState,
} from 'src/fetch/trucks';
import { getStates } from 'src/fetch/general';
// hooks
import usePageStorage from 'shared/../hooks/usePageStorage';
import { useToast } from 'shared/toast';
import LoaderSpinner from 'shared/indicators/LoaderSpinner';

const loadData = () => Promise.all([getStates(), getTruckPermitRuleTypes()]).then(([states, truckPermitRuleTypes]) => ({
  states,
  truckPermitRuleTypes,
}));

const TruckPermitRuleModal = ({ isOpen, initialValues, onClose, forceReload }) => {
  const toast = useToast();
  const {
    isLoading: isLoadingTruckPermitRuleTypes,
    isFailed,
    error,
    states,
    truckPermitRuleTypes,
  } = usePageStorage(loadData);

  const [loading, setLoading] = React.useState(true);
  const [valuesWithFiles, setValueWithFiles] = React.useState(initialValues);
  React.useEffect(() => {
    if (isNil(initialValues.id)) {
      return setLoading(false);
    }
    getTruckPermitRuleFiles(initialValues.id).then((res) => {
      setValueWithFiles({ ...initialValues, files: res.data });
      setLoading(false);
    });
  }, [initialValues.id]);

  const handleSubmit = async (values) => {
    const ruleIsNew = !values.id;
    const ruleId = await upsertTruckPermitRule(values)
      .then((res) => {
        const message = 'Saved Successfully';
        toast.success({ message });
        return res.id;
      })
      .catch((error) => {
        const messagePrefix = 'Save Failed.';
        toast.error({
          message: <>
            <b>{messagePrefix}</b>
            <p>{error.response?.data?.Detail || error}</p>
          </>,
        });
        throw error;
      });

    if (!ruleIsNew) {
      return onClose();
    }

    const statePromises = values.truckPermitStates?.map(({ state, truckPermitCounties }) => upsertTruckPermitRuleState(ruleId, { ...state, truckPermitCounties })) ?? [];
    await Promise.all(statePromises).catch((error) => {
      const messagePrefix = 'Some Truck Permit Rule States Save Failed.';
      toast.error({
        message: <>
          <b>{messagePrefix}</b>
          <p>{error.response?.data?.Detail || error}</p>
                 </>,
      });
    });

    await addTruckPermitRuleFiles(ruleId, values.files)
      .catch((error) => {
        console.error('Some Truck Permit Rule Files Save Failed.', error);
        const messagePrefix = 'Some Truck Permit Rule States Save Failed';
        toast.error({
          message: <>
            <b>{messagePrefix}</b>
            <p>{error.response?.data?.Detail || error}</p>
                   </>,
        });
      });

    forceReload();
    onClose();
  };

  const isLoading = isLoadingTruckPermitRuleTypes || loading;

  return (
    <Modal
      isOpen={isOpen}
      title={`${isNil(initialValues?.id) ? 'Add' : 'Edit'} Truck Permit Rule`}
      handleClose={onClose}
      containerStyle="medium"
    >
      <section>
        {isFailed && (
          <ErrorMessage>
            <ErrorMessage.Title>Problem loading Truck Permit Rule. Please try again</ErrorMessage.Title>
            <ErrorMessage.Details>{error.message}</ErrorMessage.Details>
          </ErrorMessage>
        )}
        {isLoading && <LoaderSpinner />}
        {!isLoading && !isFailed && (
          <TruckPermitRuleForm
            forceReload={forceReload}
            initialValues={valuesWithFiles}
            onSubmit={handleSubmit}
            onCancel={onClose}
            states={states}
            truckPermitRuleTypes={truckPermitRuleTypes}
          />
        )}
      </section>
    </Modal>
  );
};

export default TruckPermitRuleModal;
